import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // ✅ REQUIRED: Separate async operations object
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error && data) {
          setUserProfile(data)
        }
      } catch (error) {
        console.error('Profile fetch error:', error)
      } finally {
        setProfileLoading(false)
      }
    },
    
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // ⚠️ PROTECTED FUNCTION - DO NOT MODIFY OR ADD ASYNC OPERATIONS
  // This is a Supabase auth state change listener that must remain synchronous
  const handleAuthStateChange = (event, session) => {
    // SYNC OPERATIONS ONLY - NO ASYNC/AWAIT ALLOWED
    if (session?.user) {
      setUser(session?.user)
      // Fire async operations separately - NO AWAIT
      profileOperations?.load(session?.user?.id)
    } else {
      setUser(null)
      profileOperations?.clear()
    }
    setLoading(false)
  }

  useEffect(() => {
    // Get initial session - Use Promise chain with proper error handling
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
        if (error) {
          console.error('Error getting session:', error);
          setUser(null);
        } else if (session?.user) {
          setUser(session?.user);
          profileOperations?.load(session?.user?.id);
        } else {
          setUser(null);
        }
        setLoading(false);
      })?.catch((error) => {
        console.error('Session fetch error:', error);
        setUser(null);
        setLoading(false);
      });

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(handleAuthStateChange)

    return () => subscription?.unsubscribe()
  }, [])

  // Enhanced helper function to get current user safely with session check
  const getCurrentUser = async () => {
    try {
      // First check if we have a session
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError) {
        console.error('Error getting session:', sessionError);
        return null;
      }
      
      // If no session exists, return null instead of calling getUser
      if (!session) {
        console.log('No active session found');
        return null;
      }

      // Only call getUser if we have an active session
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError) {
        console.error('Error getting user:', userError);
        return null;
      }
      
      return user;
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  };

  // Helper function to check authentication status
  const isAuthenticated = () => {
    return user !== null && user !== undefined;
  };

  // Helper function to get user safely without API calls
  const getLocalUser = () => {
    return user;
  };

  // Helper function to check if current user is admin
  const isAdmin = () => {
    // Check multiple sources for admin role
    const authMetaRole = user?.user_metadata?.role || user?.raw_user_meta_data?.role;
    const appMetaRole = user?.app_metadata?.role || user?.raw_app_meta_data?.role;
    const profileRole = userProfile?.role;
    
    return authMetaRole === 'admin' || appMetaRole === 'admin' || profileRole === 'admin';
  };

  // Auth service methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Update last login timestamp
      if (data?.user) {
        try {
          await supabase?.rpc('update_last_login');
        } catch (loginError) {
          console.error('Error updating last login:', loginError);
        }
      }
      
      return { user: data?.user, session: data?.session };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        throw new Error('Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.');
      }
      throw error;
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      return { user: data?.user, session: data?.session };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        throw new Error('Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.');
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    getCurrentUser,
    isAuthenticated,
    getLocalUser,
    isAdmin,
    signIn,
    signUp,
    signOut
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}